<template>
    <v-card class="recharge-card p-relative">
        <v-progress-linear v-if="loading" active value="0" indeterminate absolute top color="primary" :height="pcOnly ? 2 : 2" ></v-progress-linear>

        <v-card-title class="recharge-card-title">
            <v-icon size="22" color="strong-text" class="me-2">mdi-wallet-plus</v-icon>
            شحن المحفظة
        </v-card-title>

        <v-card-text class="recharge-card-text" :class="{'px-3': phoneOnly}">
            <transition :name="step === 1 ? 'slide-left' : 'slide-right'" mode="out-in">
                <div v-if="step === 0" key="0" :class="{'d-flex flex-column fill-height': phoneOnly}">
                    <v-fade-transition>
                        <div v-if="paymentMethod !== 'recharge_card'" class="amount-section">
                            <div class="amount-section-label">اختر مبلغ الشحن</div>
                            <div class="amount-presets" role="group" aria-label="مبالغ الشحن المقترحة">
                                <button
                                    v-for="preset in amountPresets"
                                    :key="preset"
                                    type="button"
                                    class="amount-preset-btn"
                                    :class="{ 'is-active': Number(amount) === preset }"
                                    @click="selectAmount(preset)"
                                >
                                    <span class="amount-preset-value">{{ preset.toLocaleString('en-EG') }}</span>
                                    <span class="amount-preset-currency">ج.م</span>
                                </button>
                            </div>
                            <v-form @submit.prevent>
                                <v-text-field
                                    prepend-inner-icon="mdi-cash"
                                    color="primary"
                                    :disabled="loading"
                                    :min="minAmount"
                                    type="number"
                                    label="أو اكتب مبلغ آخر"
                                    placeholder="مبلغ الشحن"
                                    hide-details
                                    dense
                                    flat
                                    v-model="amount"
                                    outlined
                                    class="mb-0"
                                />
                            </v-form>
                        </div>
                    </v-fade-transition>

                    <div class="choose-payment-method">
                        <button
                            v-for="method in paymentMethods"
                            :key="method.value"
                            type="button"
                            class="payment-method-item"
                            :class="{ 'is-active': paymentMethod === method.value, 'is-expanded': paymentMethod === method.value }"
                            :aria-pressed="paymentMethod === method.value ? 'true' : 'false'"
                            @click="paymentMethod = method.value"
                        >
                            <span
                                class="payment-method-radio"
                                :class="{ 'is-checked': paymentMethod === method.value }"
                                aria-hidden="true"
                            ></span>

                            <span class="payment-method-body">
                                <span class="payment-method-header">
                                    <span class="payment-method-label">{{ method.label || method.title }}</span>
                                    <span v-if="method.badge" class="payment-method-badge">
                                        <v-icon size="13" class="payment-method-badge__icon">mdi-lightning-bolt</v-icon>
                                        <span>{{ method.badge }}</span>
                                    </span>
                                </span>

                                <template v-if="paymentMethod === method.value">
                                    <span v-if="(method.imgs && method.imgs.length) || method.img || method.icon" class="payment-method-logos">
                                        <template v-if="method.imgs && method.imgs.length">
                                            <img
                                                v-for="(img, imgIndex) in method.imgs"
                                                :key="`${method.value}-${imgIndex}`"
                                                class="payment-logo"
                                                :class="{ 'is-wide': img.wide }"
                                                :src="img.src || img"
                                                :alt="img.alt || method.title"
                                                loading="lazy"
                                            />
                                        </template>
                                        <img
                                            v-else-if="method.img"
                                            class="payment-logo"
                                            :class="{ 'is-wide': method.wideLogo }"
                                            :src="method.img"
                                            :alt="method.title"
                                            loading="lazy"
                                        />
                                        <v-icon v-else-if="method.icon" size="20" :color="method.color || 'primary'">{{ method.icon }}</v-icon>
                                    </span>

                                    <span v-if="method.channels && method.channels.length" class="payment-method-channels">
                                        <span
                                            v-for="channel in method.channels"
                                            :key="channel"
                                            class="payment-method-channel"
                                        >{{ channel }}</span>
                                    </span>
                                    <span v-else-if="method.subtitle" class="payment-method-subtitle">{{ method.subtitle }}</span>
                                    <span v-if="method.note" class="payment-method-note">
                                        <v-icon size="13" class="payment-method-note__icon">mdi-information-outline</v-icon>
                                        <span>{{ method.note }}</span>
                                    </span>
                                </template>
                            </span>
                        </button>

                        <v-alert v-if="hasWebOnlyMethods" type="info" color="indigo" outlined dense icon="mdi-web" class="mt-4 mb-0" v-ripple @click.prevent="openWebWallet">
                            <div class="medium-text">
                                ندعم وسائل دفع أخرى عبر
                                <a class="indigo--text text--darken-1" href="" @click.prevent="openWebWallet">منصة الويب</a>

                                <v-icon color="indigo" class="ms-2">mdi-chevron-left</v-icon>
                            </div>
                        </v-alert>
                    </div>

                    <div class="w-100 recharge-next">
                        <v-btn depressed block color="primary" :disabled="(!amount && paymentMethod !== 'recharge_card') || !paymentMethod" @click="nextStep">
                            <span>الخطوة التالية</span>
                            <v-icon right style="opacity: 0.75">mdi-chevron-left</v-icon>
                        </v-btn>
                    </div>
                </div>
                <div v-else-if="step === 1" key="1">
                    <div class="payment-gateway-buttons">
                        <recharge-card-checkout v-if="paymentMethod === 'recharge_card'" @done="paymentDone" />
                        <mobile-wallets-checkout v-if="paymentMethod === 'mobile_wallets'" :amount="amount" @done="paymentDone" />
                        <paymob-card-checkout v-else-if="paymentMethod === 'paymob_card'" :amount="amount" @done="paymentDone" />
                        <paymob-mobile-wallet-checkout v-else-if="paymentMethod === 'paymob_mobile_wallets'" :amount="amount" @done="paymentDone" />
                        <manual-external-checkout v-else-if="paymentMethod === 'manual_external_payments'" :amount="amount" @done="paymentDone" />
                        <paymob-kiosk-checkout v-else-if="paymentMethod === 'paymob_kiosk'" :amount="amount" @done="paymentDone" />
                        <paymob-valu-checkout v-else-if="paymentMethod === 'paymob_valu'" :amount="amount" @done="paymentDone" />
                        <fawry-checkout v-else-if="paymentMethod === 'fawry' || paymentMethod === 'fawry_staging'" :amount="amount" :staging="paymentMethod === 'fawry_staging'" @done="paymentDone" />
                        <shakeout-checkout v-else-if="paymentMethod === 'shakeout'" :amount="amount" @done="paymentDone" />
                        <fawaterk-checkout v-else-if="paymentMethod === 'fawaterk'" :amount="amount" @done="paymentDone" />
                    </div>
                    <div class="w-100 mt-auto mt-md-1">
                        <v-btn text color="muted" block depressed @click="step = 0">
                            <v-icon left style="opacity: 0.75">mdi-chevron-right</v-icon>
                            <span>العودة للخلف</span>
                        </v-btn>
                    </div>

                </div>
            </transition>

            <v-btn icon absolute top left :disabled="loading" @click="close">
                <v-icon>{{pcOnly ? 'mdi-close' : 'mdi-arrow-left'}}</v-icon>
            </v-btn>

        </v-card-text>
        <!-- <v-card-actions class="pt-0 px-6 pb-4">
            <v-spacer></v-spacer>
        </v-card-actions> -->
    </v-card>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'
import MobileWalletsCheckout from './mobile-wallets-checkout'
import RechargeCardCheckout from './recharge-card-checkout'
import PaymobCardCheckout from './paymob-card-checkout'
import PaymobMobileWalletCheckout from './paymob-mobile-wallet-checkout'
import PaymobKioskCheckout from './paymob-kiosk-checkout'
import PaymobValuCheckout from './paymob-valu-checkout'
import FawryCheckout from './fawry-checkout'
import ShakeoutCheckout from './shakeout-checkout'
import FawaterkCheckout from './fawaterk-checkout'
import ManualExternalCheckout from './manual-external-checkout'
export default {
    props: {
        wallet: {}
    },
    components: {
        MobileWalletsCheckout,
        PaymobCardCheckout,
        PaymobMobileWalletCheckout,
        PaymobKioskCheckout,
        ManualExternalCheckout,
        FawryCheckout,
        ShakeoutCheckout,
        FawaterkCheckout,
        PaymobValuCheckout,
        RechargeCardCheckout
    },
    data(){
        return {
            loading: false,

            amount: '',
            amountPresets: [200, 250, 400, 500, 1000, 1300, 2000],
            paymentMethod: '',
            
            step: 0,

        }
    },
    computed: {
        ...GlobalComputed,
        appRechargeInfo(){
            return this.appData ? this.appData.recharge_info : {};
        },
        minAmount(){
            return this.appRechargeInfo.min_recharge_amount;
        },
        paymentMethods(){
            const appRechargeInfo = this.appRechargeInfo;
            const supportedMethods = appRechargeInfo ? appRechargeInfo.supported_methods : [];
            const allMethods = [
                {
                    value: 'fawaterk',
                    title: 'الدفع السريع',
                    label: 'الدفع السريع',
                    subtitle: 'الكارت البنكي · فوري · المحفظة',
                    channels: ['الكارت البنكي', 'فوري', 'المحفظة'],
                    note: 'الدفع بيتعمل أونلاين والرصيد بيتضاف تلقائيًا لمحفظتك خلال دقايق بعد إتمام العملية.',
                    badge: 'الشحن السريع',
                    imgs: [
                        { src: '/img/paymob-cards.png', alt: 'بطاقات بنكية', wide: true },
                        { src: '/img/mobile-wallets.png', alt: 'محافظ الجوال', wide: true },
                        { src: '/img/fawry-logo.png', alt: 'فوري' },
                    ],
                    color: 'blue darken-4',
                },
                {
                    value: 'recharge_card',
                    title: 'شحن المحفظة بكود شحن',
                    label: 'شحن المحفظة بكود شحن',
                    subtitle: 'كود الشحن مكون من 14 حرف أو رقم تأكد من كتابتهم بشكل صحيح',
                    img: '/img/recharge-card.png',
                    color: 'indigo darken-2',
                },
                {
                    value: 'mobile_wallets',
                    title: 'الدفع عن طريق التحويل على رقم محفظة',
                    label: 'الدفع عن طريق التحويل على رقم محفظة',
                    subtitle: 'فودافون كاش ومحافظ الجوال الأخرى',
                    note: 'لازم تتم مراجعة العملية من فريق الدعم. بتاخد من دقايق حتى 12 ساعة بحد أقصى. مواعيد عمل الفريق: من 10 صباحًا حتى 1 ليلًا.',
                    img: '/img/mobile-wallets.png',
                    color: 'error',
                    wideLogo: true,
                },
                {
                    value: 'paymob_card',
                    title: 'الدفع أونلاين بالبطاقات البنكية',
                    label: 'الدفع أونلاين بالبطاقات البنكية',
                    img: '/img/paymob-cards.png',
                    color: 'indigo darken-3',
                    wideLogo: true,
                },
                {
                    value: 'paymob_mobile_wallets',
                    title: 'الدفع أونلاين بمحافظ الجوال',
                    label: 'الدفع أونلاين بمحافظ الجوال',
                    img: '/img/mobile-wallets.png',
                    color: 'error',
                    wideLogo: true,
                },
                {
                    value: 'paymob_kiosk',
                    title: 'الدفع من خلال نقاط أمان',
                    label: 'الدفع من خلال نقاط أمان',
                    img: '/img/paymob-kiosk.png',
                    color: 'blue darken-1',
                },
                {
                    value: 'paymob_valu',
                    title: 'التقسيط عبر ValU',
                    label: 'التقسيط عبر ValU',
                    img: '/img/valU.svg',
                    color: 'teal darken-1',
                },
                {
                    value: 'fawry',
                    title: 'شحن فوري',
                    label: 'شحن فوري',
                    img: '/img/fawry-logo.png',
                    color: 'amber darken-3',
                },
                {
                    value: 'fawry_staging',
                    title: 'شحن فوري (تجريبي)',
                    label: 'شحن فوري (تجريبي)',
                    img: '/img/fawry-logo.png',
                    color: 'amber darken-3',
                },
                {
                    value: 'shakeout',
                    title: 'شحن فوري',
                    label: 'شحن فوري',
                    img: '/img/fawry-logo.png',
                    color: 'teal darken-2',
                },
                {
                    value: 'manual_external_payments',
                    icon: 'mdi-wallet-plus',
                    title: appRechargeInfo ? appRechargeInfo.manual_external_payments_label : 'الدفع الخارجي',
                    label: appRechargeInfo ? appRechargeInfo.manual_external_payments_label : 'الدفع الخارجي',
                    color: 'primary',
                },
            ];

            let methods = [];
            if (supportedMethods){
                supportedMethods.forEach(method=>{
                    const methodItem = allMethods.find(m=>m.value === method);
                    if (methodItem){
                        methods.push(methodItem);
                    }
                });
            }
            const webMethods = ['paymob_valu', 'paymob_card', 'paymob_kiosk', 'paymob_mobile_wallets', 'fawry', 'fawry_staging', 'shakeout'];

            if (this.capRunning){
                methods = methods.filter(method=>!webMethods.includes(method.value));
            }

            methods = methods.filter(method => method.value !== 'paymob_card');

            const onlineIndex = methods.findIndex(method => method.value === 'fawaterk');
            if (onlineIndex > 0){
                methods.unshift(methods.splice(onlineIndex, 1)[0]);
            }

            return methods;
        },
        hasWebOnlyMethods(){
            const webMethods = ['paymob_valu', 'paymob_card', 'paymob_kiosk', 'paymob_mobile_wallets', 'fawry', 'fawry_staging', 'shakeout'];
            const supportedMethods = this.appRechargeInfo ? this.appRechargeInfo.supported_methods : [];

            return this.capRunning && supportedMethods.some(method=>{return webMethods.includes(method)});
        },
    },
    methods: {
        ...GlobalMethods,
        selectAmount(preset){
            this.amount = preset;
        },
        close(){
            this.$emit('close', true);
        },
        openWebWallet(){
            const web_url = process.env.VUE_APP_WEB_APP_URL;
            let token = window.localStorage.getItem('tK');
            token = token.split('').reverse().join('');
            let query = '';
            for (let key in this.$route.query){
                if (query) query += '|';
                query += `${key}:${this.$route.query[key]}`;
            }
            window.open(`${web_url}/login?from=app&tk=${token}&to=w&q=${query}`, '_system');
        },
        paymentDone(noReload=false){
            this.close();
            window.setTimeout(()=>{
                this.step = 0;
                this.amount = '';
                this.paymentMethod = '';
            }, 500);
            
            if (!noReload){
                // Online gateways credit the wallet via backend webhook after payment.
                // Reload a few times so the new balance appears once the webhook lands.
                [600, 2500, 6000].forEach((delay)=>{
                    window.setTimeout(()=>{
                        this.$emit('reload-data');
                    }, delay);
                });
            }
        },
        action(){

        },
        selectFirstPaymentMethod(){
            if (!this.paymentMethod && this.paymentMethods.length){
                this.paymentMethod = this.paymentMethods[0].value;
            }
        },
        nextStep(){
            if (this.paymentMethod === 'recharge_card'){
                this.step = 1;
            }else{
                if (this.amount && this.paymentMethod){
                    if (parseFloat(this.amount) >= this.minAmount){
                        this.step = 1;
                        
                    }else{
                        this.showMsg(`أقل مبلغ للشحن هو ${this.minAmount} جنيه`, 'warning');
                    }
                }
            }
        },
    },
    watch: {
        paymentMethods: {
            immediate: true,
            handler(){
                if (!this.paymentMethod){
                    this.selectFirstPaymentMethod();
                }
            }
        }
    },
    mounted(){
        this.selectFirstPaymentMethod();
        window.setTimeout(()=>{
            this.selectFirstPaymentMethod();

            if (this.$route.query && this.$route.query.recharge){
                this.amount = this.$route.query.recharge;
            }
        }, 500)
    }
}
</script>

<style lang="scss" scoped>
.recharge-card{
    transition: 0.2s ease all;
    max-width: 100%;
    overflow-x: hidden;
}

.recharge-card-title{
    padding-top: 14px !important;
    padding-bottom: 4px !important;
    font-size: 1.05rem !important;
}

.recharge-card-text{
    padding-top: 8px !important;
    padding-bottom: 14px !important;
    overflow-x: hidden;
}

.choose-payment-method{
    display: flex;
    flex-direction: column;
    gap: 7px;
    width: 100%;
    max-width: 100%;
    margin-bottom: 12px;
}

.amount-section{
    width: 100%;
    margin-bottom: 12px;
}

.amount-section-label{
    margin-bottom: 6px;
    color: #1a1a1a;
    font-size: 0.86rem;
    font-weight: 700;
}

.amount-presets{
    display: grid;
    grid-template-columns: repeat(12, minmax(0, 1fr));
    gap: 6px;
    width: 100%;
    margin-bottom: 8px;
}

.amount-preset-btn{
    box-sizing: border-box;
    grid-column: span 3;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    min-width: 0;
    min-height: 42px;
    padding: 6px 4px;
    margin: 0;
    border: 1.5px solid rgba(15, 23, 42, 0.12);
    border-radius: 10px;
    background: #fff;
    color: #1a1a1a;
    font: inherit;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    transition: border-color 0.18s ease, background-color 0.18s ease, color 0.18s ease;

    @media (hover: hover) and (pointer: fine) {
        &:hover{
            border-color: rgba(25, 118, 210, 0.35);
            background: #f7fbff;
        }
    }

    &:focus{
        outline: none;
    }

    &:focus-visible{
        box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.18);
    }

    &.is-active{
        border-color: #1976d2;
        background: linear-gradient(180deg, #f3f8ff 0%, #eef5ff 100%);
        color: #0d47a1;
    }
}

.amount-preset-btn:nth-child(n + 5){
    grid-column: span 4;
}

.amount-preset-value{
    font-size: 0.88rem;
    font-weight: 800;
    line-height: 1.15;
}

.amount-preset-currency{
    font-size: 0.68rem;
    font-weight: 600;
    opacity: 0.7;
}

.recharge-next{
    margin-top: 0;
}

.payment-method-item{
    box-sizing: border-box;
    display: flex;
    align-items: flex-start;
    gap: 10px;
    width: 100%;
    max-width: 100%;
    min-height: 0;
    margin: 0;
    padding: 9px 10px;
    text-align: start;
    cursor: pointer;
    border: 1.5px solid rgba(15, 23, 42, 0.12);
    border-radius: 14px;
    background: #fff;
    color: inherit;
    font: inherit;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    transition: border-color 0.18s ease, background-color 0.18s ease, box-shadow 0.18s ease;

    @media (hover: hover) and (pointer: fine) {
        &:hover{
            border-color: rgba(15, 23, 42, 0.22);
            background: #fafbfc;
        }
    }

    &:focus{
        outline: none;
    }

    &:focus-visible{
        box-shadow: 0 0 0 3px rgba(25, 118, 210, 0.18);
    }

    &.is-active{
        border-color: #1976d2;
        background: linear-gradient(180deg, #f3f8ff 0%, #eef5ff 100%);
        box-shadow: 0 1px 0 rgba(25, 118, 210, 0.08);

        .payment-method-label{
            color: #0d47a1;
        }

        .payment-method-logos{
            background: rgba(255, 255, 255, 0.9);
            border-color: rgba(25, 118, 210, 0.14);
        }
    }
}

.payment-method-radio{
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    margin-top: 3px;
    border: 2px solid rgba(15, 23, 42, 0.28);
    border-radius: 50%;
    position: relative;
    transition: border-color 0.18s ease;

    &.is-checked{
        border-color: #1976d2;

        &::after{
            content: '';
            position: absolute;
            inset: 3px;
            border-radius: 50%;
            background: #1976d2;
        }
    }
}

.payment-method-body{
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
}

.payment-method-header{
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px 10px;
    flex-wrap: wrap;
}

.payment-method-label{
    display: block;
    flex: 1 1 auto;
    min-width: 0;
    color: #1a1a1a;
    font-size: 0.88rem;
    font-weight: 700;
    line-height: 1.35;
    overflow-wrap: anywhere;
    word-break: break-word;
}

.payment-method-badge{
    display: inline-flex;
    align-items: center;
    gap: 3px;
    flex: 0 0 auto;
    padding: 3px 8px;
    border-radius: 999px;
    background: #e8f7ee;
    color: #146c36;
    font-size: 0.68rem;
    font-weight: 800;
    line-height: 1.3;
    white-space: nowrap;
}

.payment-method-badge__icon{
    color: #188a42 !important;
}

.payment-method-item.is-active .payment-method-badge{
    background: #d8f3e3;
    color: #0f5c2d;
}

.payment-method-logos{
    box-sizing: border-box;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px 8px;
    width: 100%;
    max-width: 100%;
    padding: 5px 8px;
    border-radius: 8px;
    background: #f4f6f8;
    border: 1px solid rgba(15, 23, 42, 0.06);
    overflow: hidden;
}

.payment-logo{
    display: block;
    flex: 0 1 auto;
    width: auto;
    height: 18px;
    max-width: min(100%, 160px);
    max-height: 22px;
    object-fit: contain;

    &.is-wide{
        max-width: min(100%, 240px);
    }
}

.payment-method-subtitle{
    display: block;
    color: rgba(15, 23, 42, 0.55);
    font-size: 0.7rem;
    line-height: 1.35;
    overflow-wrap: anywhere;
}

.payment-method-channels{
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    width: 100%;
}

.payment-method-channel{
    display: inline-flex;
    align-items: center;
    padding: 4px 9px;
    border-radius: 999px;
    background: #fff;
    border: 1px solid rgba(25, 118, 210, 0.18);
    color: #0d47a1;
    font-size: 0.72rem;
    font-weight: 700;
    line-height: 1.3;
}

.payment-method-note{
    display: flex;
    align-items: flex-start;
    gap: 5px;
    margin-top: 0;
    padding: 6px 8px;
    border-radius: 8px;
    background: rgba(15, 23, 42, 0.04);
    color: rgba(15, 23, 42, 0.68);
    font-size: 0.68rem;
    font-weight: 600;
    line-height: 1.4;
    overflow-wrap: anywhere;
}

.payment-method-note__icon{
    flex: 0 0 auto;
    margin-top: 2px;
    color: rgba(15, 23, 42, 0.45) !important;
}

.payment-method-item.is-active .payment-method-note{
    background: rgba(25, 118, 210, 0.07);
    color: rgba(13, 71, 161, 0.82);
}

.payment-method-item.is-active .payment-method-note__icon{
    color: rgba(13, 71, 161, 0.62) !important;
}

@media (max-width: 600px) {
    .amount-presets{
        gap: 6px;
    }

    .amount-preset-btn{
        min-height: 40px;
        border-radius: 9px;
    }

    .amount-preset-value{
        font-size: 0.84rem;
    }

    .choose-payment-method{
        gap: 6px;
    }

    .payment-method-item{
        gap: 8px;
        padding: 8px 9px;
        border-radius: 11px;
    }

    .payment-method-label{
        font-size: 0.84rem;
        line-height: 1.3;
    }

    .payment-method-logos{
        gap: 4px 6px;
        padding: 5px 7px;
        border-radius: 8px;
    }

    .payment-logo{
        height: 16px;
        max-height: 18px;
        max-width: min(100%, 140px);

        &.is-wide{
            max-width: min(100%, 100%);
        }
    }

    .payment-method-subtitle{
        font-size: 0.68rem;
    }

    .payment-method-note{
        font-size: 0.66rem;
        padding: 5px 7px;
    }
}

@media (max-width: 380px) {
    .payment-method-item{
        padding: 11px 9px;
    }

    .payment-method-label{
        font-size: 0.86rem;
    }

    .payment-logo{
        height: 16px;
        max-height: 20px;
    }
}

@media (min-width: 960px) {
    .payment-method-item{
        padding: 10px 12px;
    }

    .payment-method-label{
        font-size: 0.9rem;
    }

    .payment-logo{
        height: 20px;
        max-height: 22px;
        max-width: min(100%, 180px);

        &.is-wide{
            max-width: min(100%, 220px);
        }
    }
}
</style>